// Mock data for Chain Filer frontend development

export const currentUser = {
  id: 1,
  displayName: "Jeff Trading",
  slug: "jeff-trading",
  city: "Lagos",
  country: "Nigeria",
  tier: "premium" as const,
  kycStatus: "verified" as const,
  totalTrades: 340,
  verifiedVolumeUsd: 2_400_000,
  completionRate: 98.2,
  avgSettlementSecs: 420,
  reviewScore: 4.8,
  reviewCount: 127,
  isBlacklisted: false,
  pendingTradeCount: 1,
  pendingNgnTotal: 16_450_000,
};

export type TradeAttachment = {
  id: string;
  tradeId: number;
  filename: string;       // UUID-based key on R2
  originalName: string;   // display name
  url: string;            // R2 public URL
  contentType: string;
  size: number;
  uploadedBy: string;
  uploadedAt: string;
};

export type Trade = {
  id: number;
  counterpartyName: string | null;
  counterpartyId: number | null;
  direction: "buy" | "sell" | "lend" | "borrow" | "return" | "personal" | "lock" | "middleman";
  usdtAmount: number;
  rate: number | null;
  ngnAmount: number | null;
  txHash: string | null;
  chain: "tron" | "bsc" | "ethereum";
  viaExchange: boolean;
  exchangeName: string | null;
  usdtVerified: boolean;
  nairaPaymentMethod: string | null;
  nairaBankVerified: boolean;
  nairaReceiptPath: string | null;
  nairaBankRef: string | null;
  senderConfirmed: boolean;
  receiverConfirmed: boolean;
  status: "pending" | "settled" | "disputed" | "expired";
  verificationLevel: "fully_verified" | "partially_verified" | "unverified" | "flagged";
  settlementTimeSecs: number | null;
  createdAt: string;
  settledAt: string | null;
  senderAddress: string | null;
  senderEntity: string | null;
  coinStatus: "pending" | "partial" | "complete" | "overpaid";
  coinAmountSettled: number;
  nairaStatus: "pending" | "partial" | "complete" | "overpaid";
  nairaAmountSettled: number;
  isRefund?: boolean;
  refundOfTradeId?: number;
  // Middleman deal fields
  buyRate?: number | null;
  sellRate?: number | null;
  buyCounterpartyName?: string | null;
  buyCounterpartyId?: number | null;
  sellCounterpartyName?: string | null;
  sellCounterpartyId?: number | null;
  middlemanProfit?: number | null;
  nairaFlow?: "through_you" | "direct" | null;
  notes?: string | null;
  attachments?: TradeAttachment[];
};

export const recentTrades: Trade[] = [];

export type KycLevel = "unverified" | "basic" | "full" | "enhanced";

export type KycVerificationStatus = "pending" | "under_review" | "approved" | "rejected" | "expired";

export type KycDocument = {
  id: number;
  counterpartyId: number;
  docType: "national_id" | "passport" | "drivers_license" | "utility_bill" | "bank_statement" | "selfie" | "gov_id_front" | "gov_id_back" | "address_proof";
  label: string;
  uploadedAt: string;
  verified: boolean;
  expiresAt: string | null;
  filePath?: string;
};

export type KycDocumentType = "nin_slip" | "international_passport" | "drivers_license" | "voters_card";

export const DOCUMENT_TYPE_CONFIG: Record<KycDocumentType, { label: string; requiresBack: boolean }> = {
  nin_slip: { label: "NIN Slip", requiresBack: false },
  international_passport: { label: "International Passport", requiresBack: false },
  drivers_license: { label: "Driver's License", requiresBack: true },
  voters_card: { label: "Voter's Card", requiresBack: true },
};

export type KycVerification = {
  id: string;
  counterpartyId: number;
  status: KycVerificationStatus;

  // Identity
  submittedName: string | null;
  nin: string | null;
  bvn: string | null;
  dateOfBirth: string | null;
  phone: string | null;

  // Current address (user-submitted)
  currentCountry: string | null;
  currentAddress: string | null;
  currentCity: string | null;
  currentLga: string | null;
  currentState: string | null;

  // NIN/BVN API responses
  ninVerified: boolean;
  ninName: string | null;
  ninPhoto: string | null;
  ninMiddleName: string | null;
  ninGender: string | null;
  ninDateOfBirth: string | null;
  ninPhone: string | null;
  ninEmail: string | null;
  ninAddress: string | null;
  ninAddressLine: string | null;
  ninAddressTown: string | null;
  ninAddressLga: string | null;
  ninAddressState: string | null;
  ninBirthState: string | null;
  ninBirthLga: string | null;
  ninBirthCountry: string | null;
  bvnVerified: boolean;
  bvnName: string | null;
  bvnPhone: string | null;
  nameMatchScore: number | null;

  // Documents
  documentType: KycDocumentType | null;
  documents: {
    govIdFront: string | null;
    govIdBack: string | null;
  };

  // Liveness
  livenessCompleted: boolean;
  livenessDescriptor: number[] | null;
  livenessSessionToken: string | null;
  livenessChallenges: string[];
  livenessIntegrity: string | null;
  faceMatchScore: number | null;
  faceMatchPassed: boolean;

  // Device & Location
  ipAddress: string | null;
  geoLocation: { lat: number; lng: number; accuracy: number } | null;
  deviceFingerprint: {
    browser: string;
    os: string;
    screenRes: string;
    timezone: string;
    language: string;
    userAgent: string;
  } | null;

  // Review
  reviewedBy: number | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  kycLevel: KycLevel;

  // Timestamps
  submittedAt: string;
  approvedAt: string | null;
  rejectedAt: string | null;
  expiresAt: string | null;

  // Step tracking
  currentStep: "identity" | "documents" | "liveness" | "device" | "review" | "submitted" | "complete";

  // Shareable token for counterparty-facing KYC link
  kycToken: string | null;

  // Verification type
  verificationType: "kyc" | "kyb";

  // KYB: CAC Business Info
  cacRcNumber: string | null;
  cacCompanyName: string | null;
  cacCompanyType: string | null;
  cacRegistrationDate: string | null;
  cacDirectors: string[] | null;
  cacShareholders: string[] | null;
  cacVerified: boolean;
  cacLookupResponse: Record<string, unknown> | null;
  isDirectorConfirmed: boolean;
  submittedRole: string | null;

  // KYB: AML/PEP
  amlIndividualPassed: boolean | null;
  amlIndividualFlags: string[] | null;
  amlCompanyPassed: boolean | null;
  amlCompanyFlags: string[] | null;
  amlScreenedAt: string | null;

  // KYB step tracking
  kybStep: "business_info" | "review" | "auto_verifying" | "complete" | null;
};

export type TrustLevel = "high_risk" | "elevated" | "moderate" | "trusted";

export type VerificationCheckType =
  | "nin"
  | "bvn"
  | "liveness"
  | "face_match"
  | "pep"
  | "sanctions"
  | "adverse_media"
  | "karma"
  | "bank_name"
  | "cac"
  | "cac_directors";

export type VerificationProvider = "didit" | "dojah" | "monnify" | "lendsqr" | "manual";

export type VerificationCheckStatus = "pending" | "passed" | "failed" | "error";

export type VerificationCheck = {
  id: string;
  counterpartyId: number;
  type: VerificationCheckType;
  provider: VerificationProvider;
  status: VerificationCheckStatus;
  result: Record<string, unknown> | null;
  score: number;
  detail: string | null;
  checkedAt: string;
  checkedBy: number;
  expiresAt: string | null;
};

export type LinkedBankAccount = {
  id: string;
  counterpartyId: number;
  bankCode: string;
  bankName: string;
  accountNumber: string;
  accountName: string;
  nameMatchStatus: "match" | "mismatch" | "partial" | "unchecked";
  verifiedName: string | null;
  companyName: string | null;
  addedAt: string;
  verifiedAt: string | null;
};

export type Counterparty = {
  id: number;
  name: string;
  alias: string;
  slug: string;
  city: string;
  phone: string;
  email: string | null;
  address: string | null;
  linkedUserId: number | null;
  tradeCount: number;
  totalVolumeUsd: number;
  avgSettlementSecs: number;
  reviewScore: number;
  reviewCount: number;
  kycVerified: boolean;
  kycLevel: KycLevel;
  pendingTradeCount: number;
  pendingWithOthers: number;
  pendingNgnTotal: number;
  pendingUsdtTotal: number;
  disputes: number;
  disputesWon: number;
  isBlacklisted: boolean;
  lastActive: string;
  memberSince: string;
  completionRate: number;
  verifiedVolumeUsd: number;
  balanceNgn: number;
  balanceUsdt: number;
  // Trust & verification fields
  trustScore: number;
  trustLevel: TrustLevel;
  kycTier: number;
  lastScreenedAt: string | null;
  monitoringActive: boolean;
  bvnWatchlistClear: boolean | null;
  karmaClear: boolean | null;
  cacVerified: boolean | null;
  cacRcNumber: string | null;
  cacCompanyName: string | null;
  isDirector: boolean | null;
  notes: string | null;
  isBlocked: boolean;
  blockReason: string | null;
  blockedAt: string | null;
  photo: string | null; // URL to counterparty photo (from KYC selfie)
  amlCleared: boolean | null;
  kybVerificationId: string | null;
  // Contact history — audit trail for contact info changes
  contactHistory: ContactChange[];
};

export type ContactChange = {
  field: "phone" | "email" | "city" | "address";
  oldValue: string;
  newValue: string;
  changedAt: string;
  primary?: boolean; // was this set as primary/default
};

export const counterparties: Counterparty[] = [
  {
    id: 2,
    name: "Ahmed",
    alias: "Ahmed Trading",
    slug: "ahmed-trading",
    city: "Lagos",
    phone: "+234 801 XXX XXXX",
    email: null,
    address: null,
    linkedUserId: 2,
    tradeCount: 83,
    totalVolumeUsd: 1_200_000,
    avgSettlementSecs: 420,
    reviewScore: 4.8,
    reviewCount: 127,
    kycVerified: true,
    kycLevel: "full",
    pendingTradeCount: 1,
    pendingWithOthers: 0,
    pendingNgnTotal: 16_450_000,
    pendingUsdtTotal: 0,
    disputes: 1,
    disputesWon: 1,
    isBlacklisted: false,
    lastActive: "2026-09-18T13:14:03Z",
    memberSince: "2025-03-12T00:00:00Z",
    completionRate: 98.2,
    verifiedVolumeUsd: 1_100_000,
    balanceNgn: 0,
    balanceUsdt: 0,
    trustScore: 78,
    trustLevel: "trusted",
    kycTier: 3,
    lastScreenedAt: "2026-10-03T09:00:00Z",
    monitoringActive: true,
    bvnWatchlistClear: true,
    karmaClear: true,
    cacVerified: true,
    cacRcNumber: "RC1234567",
    cacCompanyName: "Ahmed Trading Ltd",
    isDirector: true,
    notes: null,
    isBlocked: false,
    blockReason: null,
    blockedAt: null,
    photo: null,
    amlCleared: null,
    kybVerificationId: null,
    contactHistory: [],
  },
  {
    id: 3,
    name: "Blessing",
    alias: "Blessing FX",
    slug: "blessing-fx",
    city: "Abuja",
    phone: "+234 802 XXX XXXX",
    email: null,
    address: null,
    linkedUserId: 3,
    tradeCount: 56,
    totalVolumeUsd: 890_000,
    avgSettlementSecs: 240,
    reviewScore: 4.9,
    reviewCount: 89,
    kycVerified: true,
    kycLevel: "full",
    pendingTradeCount: 3,
    pendingWithOthers: 0,
    pendingNgnTotal: 27_202_000,
    pendingUsdtTotal: 0,
    disputes: 0,
    disputesWon: 0,
    isBlacklisted: false,
    lastActive: "2026-10-04T11:00:00Z",
    memberSince: "2025-01-20T00:00:00Z",
    completionRate: 99.1,
    verifiedVolumeUsd: 847_000,
    balanceNgn: 1_200_000,
    balanceUsdt: 0,
    trustScore: 65,
    trustLevel: "trusted",
    kycTier: 1,
    lastScreenedAt: "2026-09-20T14:00:00Z",
    monitoringActive: false,
    bvnWatchlistClear: true,
    karmaClear: null,
    cacVerified: null,
    cacRcNumber: null,
    cacCompanyName: null,
    isDirector: null,
    notes: null,
    isBlocked: false,
    blockReason: null,
    blockedAt: null,
    photo: null,
    amlCleared: null,
    kybVerificationId: null,
    contactHistory: [],
  },
  {
    id: 4,
    name: "Chidi",
    alias: "Chidi",
    slug: "chidi",
    city: "Port Harcourt",
    phone: "+234 803 XXX XXXX",
    email: null,
    address: null,
    linkedUserId: 4,
    tradeCount: 29,
    totalVolumeUsd: 340_000,
    avgSettlementSecs: 720,
    reviewScore: 4.5,
    reviewCount: 34,
    kycVerified: false,
    kycLevel: "unverified",
    pendingTradeCount: 0,
    pendingWithOthers: 2,
    pendingNgnTotal: 0,
    pendingUsdtTotal: 0,
    disputes: 0,
    disputesWon: 0,
    isBlacklisted: false,
    lastActive: "2026-09-17T18:00:00Z",
    memberSince: "2025-06-05T00:00:00Z",
    completionRate: 96.5,
    verifiedVolumeUsd: 290_000,
    balanceNgn: 0,
    balanceUsdt: 0,
    trustScore: 5,
    trustLevel: "trusted",
    kycTier: 0,
    lastScreenedAt: null,
    monitoringActive: false,
    bvnWatchlistClear: null,
    karmaClear: null,
    cacVerified: null,
    cacRcNumber: null,
    cacCompanyName: null,
    isDirector: null,
    notes: null,
    isBlocked: false,
    blockReason: null,
    blockedAt: null,
    photo: null,
    amlCleared: null,
    kybVerificationId: null,
    contactHistory: [],
  },
  {
    id: 5,
    name: "Emeka",
    alias: "Big E",
    slug: "big-e",
    city: "Lagos",
    phone: "+234 805 XXX XXXX",
    email: null,
    address: null,
    linkedUserId: 5,
    tradeCount: 12,
    totalVolumeUsd: 95_000,
    avgSettlementSecs: 2040,
    reviewScore: 3.2,
    reviewCount: 8,
    kycVerified: true,
    kycLevel: "basic",
    pendingTradeCount: 0,
    pendingWithOthers: 0,
    pendingNgnTotal: 0,
    pendingUsdtTotal: 0,
    disputes: 1,
    disputesWon: 0,
    isBlacklisted: false,
    lastActive: "2026-09-13T10:00:00Z",
    memberSince: "2026-02-14T00:00:00Z",
    completionRate: 91.7,
    verifiedVolumeUsd: 72_000,
    balanceNgn: 0,
    balanceUsdt: 0,
    trustScore: 22,
    trustLevel: "trusted",
    kycTier: 1,
    lastScreenedAt: "2026-08-01T10:00:00Z",
    monitoringActive: false,
    bvnWatchlistClear: true,
    karmaClear: false,
    cacVerified: null,
    cacRcNumber: null,
    cacCompanyName: null,
    isDirector: null,
    notes: null,
    isBlocked: false,
    blockReason: null,
    blockedAt: null,
    photo: null,
    amlCleared: null,
    kybVerificationId: null,
    contactHistory: [],
  },
  {
    id: 6,
    name: "Fatima",
    alias: "Fatima Crypto",
    slug: "fatima-crypto",
    city: "Kano",
    phone: "+234 806 XXX XXXX",
    email: null,
    address: null,
    linkedUserId: 6,
    tradeCount: 145,
    totalVolumeUsd: 3_200_000,
    avgSettlementSecs: 180,
    reviewScore: 5.0,
    reviewCount: 201,
    kycVerified: true,
    kycLevel: "enhanced",
    pendingTradeCount: 0,
    pendingWithOthers: 0,
    pendingNgnTotal: 0,
    pendingUsdtTotal: 0,
    disputes: 0,
    disputesWon: 0,
    isBlacklisted: false,
    lastActive: "2026-09-18T12:00:00Z",
    memberSince: "2024-11-01T00:00:00Z",
    completionRate: 99.8,
    verifiedVolumeUsd: 3_100_000,
    balanceNgn: 200_000,
    balanceUsdt: 0,
    trustScore: 91,
    trustLevel: "trusted",
    kycTier: 3,
    lastScreenedAt: "2026-10-01T08:00:00Z",
    monitoringActive: true,
    bvnWatchlistClear: true,
    karmaClear: true,
    cacVerified: true,
    cacRcNumber: "RC7654321",
    cacCompanyName: "FA Global Trading Ltd",
    isDirector: true,
    notes: null,
    isBlocked: false,
    blockReason: null,
    blockedAt: null,
    photo: null,
    amlCleared: null,
    kybVerificationId: null,
    contactHistory: [],
  },
];

export type Alert = {
  id: number;
  type: "warning" | "danger" | "success" | "info";
  title: string;
  body: string;
  createdAt: string;
  read: boolean;
};

export const alerts: Alert[] = [
  {
    id: 1,
    type: "warning",
    title: "Pending trades detected",
    body: "Chidi has 2 pending trades with other traders (₦18M unsettled)",
    createdAt: "2026-09-18T12:00:00Z",
    read: false,
  },
  {
    id: 2,
    type: "danger",
    title: "Counterparty blacklisted",
    body: "Address 0xab3f... was added to the blacklist — you traded with this address 3 months ago",
    createdAt: "2026-09-18T10:30:00Z",
    read: false,
  },
  {
    id: 3,
    type: "success",
    title: "New review received",
    body: 'Blessing left you a 5-star review: "Fast settlement, always reliable"',
    createdAt: "2026-09-18T09:15:00Z",
    read: false,
  },
];

export const settlementPattern = [
  { label: "Under 5 min", percentage: 45, count: 153 },
  { label: "5-15 min", percentage: 42, count: 143 },
  { label: "15-30 min", percentage: 8, count: 27 },
  { label: "30-60 min", percentage: 4, count: 14 },
  { label: "Over 1 hour", percentage: 1, count: 3 },
];

export type CounterpartyWallet = {
  id: number;
  counterpartyId: number;
  chain: "tron" | "bsc" | "ethereum";
  address: string;
  isPrimary: boolean;
  linkedAt: string;
  tradeCount: number;
  lastUsed: string;
  amlRiskScore: number | null; // 0-100, null = not scanned. Lower = cleaner.
};

export const counterpartyWallets: CounterpartyWallet[] = [
  // Ahmed (id: 2) — 2 wallets
  {
    id: 1,
    counterpartyId: 2,
    chain: "tron",
    address: "TQn9Y2khEsLJW1ChVWFMSMeRDow5KcbLSE",
    isPrimary: true,
    linkedAt: "2025-03-12T00:00:00Z",
    tradeCount: 71,
    lastUsed: "2026-09-18T13:14:03Z",
    amlRiskScore: 12,
  },
  {
    id: 2,
    counterpartyId: 2,
    chain: "bsc",
    address: "0x7a3b92c1d3e5f6a7b8c9d0e1f2a3b4c5d6e7f81a",
    isPrimary: false,
    linkedAt: "2025-08-20T00:00:00Z",
    tradeCount: 12,
    lastUsed: "2026-09-10T09:30:00Z",
    amlRiskScore: 18,
  },
  // Blessing (id: 3) — 1 wallet
  {
    id: 3,
    counterpartyId: 3,
    chain: "bsc",
    address: "0x2e4f6a8b0c1d3e5f7a9b2c4d6e8f0a1b3c5d7e9f",
    isPrimary: true,
    linkedAt: "2025-01-20T00:00:00Z",
    tradeCount: 56,
    lastUsed: "2026-09-18T12:51:00Z",
    amlRiskScore: 45,
  },
  // Chidi (id: 4) — 1 wallet
  {
    id: 4,
    counterpartyId: 4,
    chain: "tron",
    address: "TPz8mGXv2n4jK9sR3wF5tY7uBcNx6hQ1dA",
    isPrimary: true,
    linkedAt: "2025-06-05T00:00:00Z",
    tradeCount: 29,
    lastUsed: "2026-09-17T12:00:00Z",
    amlRiskScore: null,
  },
  // Emeka (id: 5) — 2 wallets
  {
    id: 5,
    counterpartyId: 5,
    chain: "tron",
    address: "TJf4kN8pR2vW6xB9mC3qY5sA7uE1gH4jL0",
    isPrimary: true,
    linkedAt: "2026-02-14T00:00:00Z",
    tradeCount: 10,
    lastUsed: "2026-09-13T10:00:00Z",
    amlRiskScore: 92,
  },
  {
    id: 6,
    counterpartyId: 5,
    chain: "ethereum",
    address: "0x9f1e3d5b7a2c4e6f8d0b2a4c6e8f0a1b3d5e7f9a",
    isPrimary: false,
    linkedAt: "2026-05-01T00:00:00Z",
    tradeCount: 2,
    lastUsed: "2026-08-20T14:00:00Z",
    amlRiskScore: 86,
  },
  // Fatima (id: 6) — 3 wallets
  {
    id: 7,
    counterpartyId: 6,
    chain: "tron",
    address: "TRx8pQmN4v2kBw9Yz1cD3eF5gH7jK0mP6s",
    isPrimary: true,
    linkedAt: "2024-11-01T00:00:00Z",
    tradeCount: 120,
    lastUsed: "2026-09-17T15:34:30Z",
    amlRiskScore: 8,
  },
  {
    id: 8,
    counterpartyId: 6,
    chain: "bsc",
    address: "0xab3f1c2d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b",
    isPrimary: false,
    linkedAt: "2025-04-15T00:00:00Z",
    tradeCount: 18,
    lastUsed: "2026-09-15T08:00:00Z",
    amlRiskScore: 14,
  },
  {
    id: 9,
    counterpartyId: 6,
    chain: "ethereum",
    address: "0xcd5e7f9a1b3d5e7f9a1b3d5e7f9a1b3d5e7f9a1b",
    isPrimary: false,
    linkedAt: "2025-09-10T00:00:00Z",
    tradeCount: 7,
    lastUsed: "2026-09-12T16:30:00Z",
    amlRiskScore: 11,
  },
];

export const kycDocuments: KycDocument[] = [
  // Ahmed (id: 2) — 3 docs (Full KYC)
  {
    id: 1,
    counterpartyId: 2,
    docType: "national_id",
    label: "Nigerian National ID",
    uploadedAt: "2025-03-12T10:00:00Z",
    verified: true,
    expiresAt: "2030-03-12T00:00:00Z",
  },
  {
    id: 2,
    counterpartyId: 2,
    docType: "selfie",
    label: "Selfie with ID",
    uploadedAt: "2025-03-12T10:05:00Z",
    verified: true,
    expiresAt: null,
  },
  {
    id: 3,
    counterpartyId: 2,
    docType: "utility_bill",
    label: "PHCN Electricity Bill",
    uploadedAt: "2025-03-12T10:10:00Z",
    verified: true,
    expiresAt: null,
  },
  // Blessing (id: 3) — 3 docs (Full KYC)
  {
    id: 4,
    counterpartyId: 3,
    docType: "passport",
    label: "Nigerian Passport",
    uploadedAt: "2025-01-20T14:00:00Z",
    verified: true,
    expiresAt: "2029-01-20T00:00:00Z",
  },
  {
    id: 5,
    counterpartyId: 3,
    docType: "selfie",
    label: "Selfie with Passport",
    uploadedAt: "2025-01-20T14:05:00Z",
    verified: true,
    expiresAt: null,
  },
  {
    id: 6,
    counterpartyId: 3,
    docType: "bank_statement",
    label: "GTBank Statement (3 months)",
    uploadedAt: "2025-01-20T14:10:00Z",
    verified: true,
    expiresAt: null,
  },
  // Chidi (id: 4) — 0 docs (Unverified)
  // Emeka (id: 5) — 2 docs (Basic KYC)
  {
    id: 7,
    counterpartyId: 5,
    docType: "drivers_license",
    label: "Nigerian Driver's License",
    uploadedAt: "2026-02-14T09:00:00Z",
    verified: true,
    expiresAt: "2028-02-14T00:00:00Z",
  },
  {
    id: 8,
    counterpartyId: 5,
    docType: "selfie",
    label: "Selfie with License",
    uploadedAt: "2026-02-14T09:05:00Z",
    verified: false,
    expiresAt: null,
  },
  // Fatima (id: 6) — 4 docs (Full KYC)
  {
    id: 9,
    counterpartyId: 6,
    docType: "national_id",
    label: "Nigerian National ID",
    uploadedAt: "2024-11-01T08:00:00Z",
    verified: true,
    expiresAt: "2029-11-01T00:00:00Z",
  },
  {
    id: 10,
    counterpartyId: 6,
    docType: "selfie",
    label: "Selfie with ID",
    uploadedAt: "2024-11-01T08:05:00Z",
    verified: true,
    expiresAt: null,
  },
  {
    id: 11,
    counterpartyId: 6,
    docType: "utility_bill",
    label: "KEDCO Electricity Bill",
    uploadedAt: "2024-11-01T08:10:00Z",
    verified: true,
    expiresAt: null,
  },
  {
    id: 12,
    counterpartyId: 6,
    docType: "bank_statement",
    label: "First Bank Statement (6 months)",
    uploadedAt: "2025-06-15T11:00:00Z",
    verified: true,
    expiresAt: null,
  },
];

export const seedVerificationChecks: VerificationCheck[] = [
  // Ahmed (id: 2) — Tier 3, fully verified
  { id: "vc-ahmed-nin", counterpartyId: 2, type: "nin", provider: "dojah", status: "passed", result: { nin: "12345678901", name: "Ahmed Bello", dob: "1990-03-15" }, score: 10, detail: null, checkedAt: "2026-10-03T09:00:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-ahmed-bvn", counterpartyId: 2, type: "bvn", provider: "monnify", status: "passed", result: { bvn: "22345678901", name: "Ahmed Bello", watchlistClear: true }, score: 10, detail: null, checkedAt: "2026-10-03T09:00:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-ahmed-live", counterpartyId: 2, type: "liveness", provider: "didit", status: "passed", result: { score: 0.98 }, score: 5, detail: null, checkedAt: "2026-10-03T09:01:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-ahmed-face", counterpartyId: 2, type: "face_match", provider: "didit", status: "passed", result: { similarity: 94.2 }, score: 10, detail: "94.2% match", checkedAt: "2026-10-03T09:01:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-ahmed-pep", counterpartyId: 2, type: "pep", provider: "didit", status: "passed", result: { hits: 0 }, score: 15, detail: null, checkedAt: "2026-10-03T09:02:00Z", checkedBy: 1, expiresAt: "2027-01-03T09:02:00Z" },
  { id: "vc-ahmed-sanc", counterpartyId: 2, type: "sanctions", provider: "didit", status: "passed", result: { hits: 0 }, score: 0, detail: null, checkedAt: "2026-10-03T09:02:00Z", checkedBy: 1, expiresAt: "2027-01-03T09:02:00Z" },
  { id: "vc-ahmed-karma", counterpartyId: 2, type: "karma", provider: "lendsqr", status: "passed", result: { listed: false }, score: 10, detail: null, checkedAt: "2026-10-03T09:03:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-ahmed-cac", counterpartyId: 2, type: "cac", provider: "dojah", status: "passed", result: { companyName: "Ahmed Trading Ltd", rcNumber: "RC1234567", status: "active", type: "LLC" }, score: 5, detail: null, checkedAt: "2026-10-05T10:00:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-ahmed-dir", counterpartyId: 2, type: "cac_directors", provider: "dojah", status: "passed", result: { directors: ["Ahmed Bello", "Fatima Bello"], isDirector: true }, score: 5, detail: "Listed as director", checkedAt: "2026-10-05T10:00:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-ahmed-bank", counterpartyId: 2, type: "bank_name", provider: "monnify", status: "passed", result: { accountName: "AHMED TRADING LTD", bankCode: "058" }, score: 10, detail: "Name matches company", checkedAt: "2026-10-05T10:01:00Z", checkedBy: 1, expiresAt: null },

  // Blessing (id: 3) — Tier 1, identity only
  { id: "vc-blessing-nin", counterpartyId: 3, type: "nin", provider: "dojah", status: "passed", result: { nin: "98765432101", name: "Blessing Okonkwo" }, score: 10, detail: null, checkedAt: "2026-09-20T14:00:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-blessing-bvn", counterpartyId: 3, type: "bvn", provider: "monnify", status: "passed", result: { bvn: "88765432101", name: "Blessing Okonkwo", watchlistClear: true }, score: 10, detail: null, checkedAt: "2026-09-20T14:00:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-blessing-live", counterpartyId: 3, type: "liveness", provider: "didit", status: "passed", result: { score: 0.96 }, score: 5, detail: null, checkedAt: "2026-09-20T14:01:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-blessing-face", counterpartyId: 3, type: "face_match", provider: "didit", status: "passed", result: { similarity: 91.5 }, score: 10, detail: "91.5% match", checkedAt: "2026-09-20T14:01:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-blessing-pep", counterpartyId: 3, type: "pep", provider: "didit", status: "passed", result: { hits: 0 }, score: 15, detail: null, checkedAt: "2026-09-20T14:02:00Z", checkedBy: 1, expiresAt: "2026-12-20T14:02:00Z" },
  { id: "vc-blessing-sanc", counterpartyId: 3, type: "sanctions", provider: "didit", status: "passed", result: { hits: 0 }, score: 0, detail: null, checkedAt: "2026-09-20T14:02:00Z", checkedBy: 1, expiresAt: "2026-12-20T14:02:00Z" },

  // Chidi (id: 4) — Tier 0, no checks
  // (no entries)

  // Emeka (id: 5) — Tier 1, some failed
  { id: "vc-emeka-nin", counterpartyId: 5, type: "nin", provider: "dojah", status: "passed", result: { nin: "55512345678", name: "Emeka Nwosu" }, score: 10, detail: null, checkedAt: "2026-08-01T10:00:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-emeka-bvn", counterpartyId: 5, type: "bvn", provider: "monnify", status: "passed", result: { bvn: "33512345678", name: "Emeka Nwosu", watchlistClear: true }, score: 10, detail: null, checkedAt: "2026-08-01T10:00:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-emeka-live", counterpartyId: 5, type: "liveness", provider: "didit", status: "passed", result: { score: 0.92 }, score: 5, detail: null, checkedAt: "2026-08-01T10:01:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-emeka-face", counterpartyId: 5, type: "face_match", provider: "didit", status: "failed", result: { similarity: 12.4 }, score: -10, detail: "12.4% — below 80% threshold", checkedAt: "2026-08-01T10:01:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-emeka-karma", counterpartyId: 5, type: "karma", provider: "lendsqr", status: "failed", result: { listed: true, lenders: ["FairMoney", "Carbon"], defaults: 2 }, score: -15, detail: "2 loan defaults (FairMoney, Carbon)", checkedAt: "2026-08-01T10:02:00Z", checkedBy: 1, expiresAt: null },

  // Fatima (id: 6) — Tier 3, fully verified, trusted
  { id: "vc-fatima-nin", counterpartyId: 6, type: "nin", provider: "dojah", status: "passed", result: { nin: "77712345678", name: "Fatima Abdullahi" }, score: 10, detail: null, checkedAt: "2026-10-01T08:00:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-fatima-bvn", counterpartyId: 6, type: "bvn", provider: "monnify", status: "passed", result: { bvn: "44712345678", name: "Fatima Abdullahi", watchlistClear: true }, score: 10, detail: null, checkedAt: "2026-10-01T08:00:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-fatima-live", counterpartyId: 6, type: "liveness", provider: "didit", status: "passed", result: { score: 0.99 }, score: 5, detail: null, checkedAt: "2026-10-01T08:01:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-fatima-face", counterpartyId: 6, type: "face_match", provider: "didit", status: "passed", result: { similarity: 97.8 }, score: 10, detail: "97.8% match", checkedAt: "2026-10-01T08:01:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-fatima-pep", counterpartyId: 6, type: "pep", provider: "didit", status: "passed", result: { hits: 0 }, score: 15, detail: null, checkedAt: "2026-10-01T08:02:00Z", checkedBy: 1, expiresAt: "2027-01-01T08:02:00Z" },
  { id: "vc-fatima-sanc", counterpartyId: 6, type: "sanctions", provider: "didit", status: "passed", result: { hits: 0 }, score: 0, detail: null, checkedAt: "2026-10-01T08:02:00Z", checkedBy: 1, expiresAt: "2027-01-01T08:02:00Z" },
  { id: "vc-fatima-karma", counterpartyId: 6, type: "karma", provider: "lendsqr", status: "passed", result: { listed: false }, score: 10, detail: null, checkedAt: "2026-10-01T08:03:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-fatima-cac", counterpartyId: 6, type: "cac", provider: "dojah", status: "passed", result: { companyName: "FA Global Trading Ltd", rcNumber: "RC7654321", status: "active", type: "LLC" }, score: 5, detail: null, checkedAt: "2026-10-01T08:04:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-fatima-dir", counterpartyId: 6, type: "cac_directors", provider: "dojah", status: "passed", result: { directors: ["Fatima Abdullahi", "Ibrahim Abdullahi"], isDirector: true }, score: 5, detail: "Listed as director", checkedAt: "2026-10-01T08:04:00Z", checkedBy: 1, expiresAt: null },
  { id: "vc-fatima-bank", counterpartyId: 6, type: "bank_name", provider: "monnify", status: "passed", result: { accountName: "FA GLOBAL TRADING LTD", bankCode: "011" }, score: 10, detail: "Name matches company", checkedAt: "2026-10-01T08:05:00Z", checkedBy: 1, expiresAt: null },
];

export const seedBankAccounts: LinkedBankAccount[] = [
  // Ahmed — 2 bank accounts
  { id: "ba-ahmed-1", counterpartyId: 2, bankCode: "058", bankName: "GTBank", accountNumber: "0123456789", accountName: "AHMED TRADING LTD", nameMatchStatus: "match", verifiedName: "Ahmed Bello", companyName: "Ahmed Trading Ltd", addedAt: "2025-03-12T00:00:00Z", verifiedAt: "2026-10-05T10:01:00Z" },
  { id: "ba-ahmed-2", counterpartyId: 2, bankCode: "044", bankName: "Access Bank", accountNumber: "9876543210", accountName: "AHMED BELLO", nameMatchStatus: "match", verifiedName: "Ahmed Bello", companyName: null, addedAt: "2025-08-20T00:00:00Z", verifiedAt: "2026-10-05T10:02:00Z" },
  // Blessing — 1 bank account
  { id: "ba-blessing-1", counterpartyId: 3, bankCode: "058", bankName: "GTBank", accountNumber: "5554443332", accountName: "BLESSING OKONKWO", nameMatchStatus: "match", verifiedName: "Blessing Okonkwo", companyName: null, addedAt: "2025-01-20T00:00:00Z", verifiedAt: "2026-09-20T14:05:00Z" },
  // Emeka — 1 bank account (mismatch!)
  { id: "ba-emeka-1", counterpartyId: 5, bankCode: "057", bankName: "Zenith Bank", accountNumber: "5555555555", accountName: "JOHN DOE", nameMatchStatus: "mismatch", verifiedName: "Emeka Nwosu", companyName: null, addedAt: "2026-02-14T00:00:00Z", verifiedAt: "2026-08-01T10:05:00Z" },
  // Fatima — 2 bank accounts
  { id: "ba-fatima-1", counterpartyId: 6, bankCode: "011", bankName: "First Bank", accountNumber: "1112223334", accountName: "FA GLOBAL TRADING LTD", nameMatchStatus: "match", verifiedName: "Fatima Abdullahi", companyName: "FA Global Trading Ltd", addedAt: "2024-11-01T00:00:00Z", verifiedAt: "2026-10-01T08:05:00Z" },
  { id: "ba-fatima-2", counterpartyId: 6, bankCode: "058", bankName: "GTBank", accountNumber: "7778889990", accountName: "FATIMA ABDULLAHI", nameMatchStatus: "match", verifiedName: "Fatima Abdullahi", companyName: null, addedAt: "2025-04-15T00:00:00Z", verifiedAt: "2026-10-01T08:06:00Z" },
];

export type TradeReviewReply = {
  id: string;
  reviewId: string;
  text: string;
  createdAt: string;
  createdBy: string;
};

export type TradeReview = {
  id: string;
  tradeId: number;
  counterpartyId: number;
  counterpartyName: string;
  tags: string[];
  sentiment: "positive" | "mixed" | "negative";
  note: string | null;
  anonymous: boolean;
  tradeAmount: number;
  settlementTimeSecs: number | null;
  createdAt: string;
  replies?: TradeReviewReply[];
};

/** @deprecated Use TradeReview instead */
export type Review = TradeReview;

export const POSITIVE_TAGS = [
  { id: "paid_fast", label: "Paid fast" },
  { id: "sharp_person", label: "Sharp person" },
  { id: "coin_landed_quick", label: "Coin landed quick" },
  { id: "smooth_deal", label: "Smooth deal" },
  { id: "good_rate", label: "Good rate" },
  { id: "would_trade_again", label: "Would trade again" },
] as const;

export const NEGATIVE_TAGS = [
  { id: "slow_payment", label: "Slow payment" },
  { id: "held_my_coin", label: "Held my coin" },
  { id: "third_party_account", label: "Third-party account" },
  { id: "name_mismatch", label: "Name mismatch" },
  { id: "rate_wahala", label: "Rate wahala" },
  { id: "had_to_chase", label: "Had to chase" },
] as const;

export const POSITIVE_TAG_IDS: Set<string> = new Set(POSITIVE_TAGS.map((t) => t.id));

export const ALL_TAG_IDS: string[] = [
  ...POSITIVE_TAGS.map((t) => t.id),
  ...NEGATIVE_TAGS.map((t) => t.id),
];

export function computeSentiment(tags: string[]): TradeReview["sentiment"] {
  const pos = tags.filter((t) => POSITIVE_TAG_IDS.has(t)).length;
  const neg = tags.length - pos;
  if (neg === 0) return "positive";
  if (pos === 0) return "negative";
  return "mixed";
}

export const reviews: TradeReview[] = [
  {
    id: "rev-001",
    tradeId: 2,
    counterpartyId: 3,
    counterpartyName: "Blessing",
    tags: ["paid_fast", "smooth_deal", "would_trade_again"],
    sentiment: "positive",
    note: "Fast settlement, always reliable",
    anonymous: false,
    tradeAmount: 15_000,
    settlementTimeSecs: 240,
    createdAt: "2026-09-16T00:00:00Z",
  },
  {
    id: "rev-002",
    tradeId: 12,
    counterpartyId: 4,
    counterpartyName: "Chidi",
    tags: ["paid_fast", "sharp_person", "coin_landed_quick"],
    sentiment: "positive",
    note: "Traded 50k, settled in 4 minutes",
    anonymous: false,
    tradeAmount: 50_000,
    settlementTimeSecs: 240,
    createdAt: "2026-09-14T00:00:00Z",
  },
  {
    id: "rev-003",
    tradeId: 18,
    counterpartyId: 6,
    counterpartyName: "Emeka",
    tags: ["good_rate", "slow_payment"],
    sentiment: "mixed",
    note: "Good but was 20 min late once",
    anonymous: false,
    tradeAmount: 8_000,
    settlementTimeSecs: 1200,
    createdAt: "2026-08-28T00:00:00Z",
  },
  {
    id: "rev-004",
    tradeId: 5,
    counterpartyId: 5,
    counterpartyName: "Fatima",
    tags: ["paid_fast", "sharp_person", "smooth_deal", "would_trade_again"],
    sentiment: "positive",
    note: "Been trading with this person for 1 year, zero issues. Top tier OTC trader.",
    anonymous: false,
    tradeAmount: 100_000,
    settlementTimeSecs: 180,
    createdAt: "2026-08-20T00:00:00Z",
  },
];

// --- Balance Adjustments (counterparty account settlements) ---

export type BalanceAdjustment = {
  id: string;
  counterpartyId: number;
  type: "payout" | "collection" | "netting";
  amount: number;
  currency: "NGN" | "USDT";
  paymentMethod: "bank_transfer" | "cash" | "crypto_wallet";
  destination: string;
  reference: string;
  payerDetailId?: string;
  crossCurrencyRate?: number;
  ngnAmount?: number;
  usdtAmount?: number;
  createdAt: string;
  // Context at time of settlement
  note?: string;
  balanceBefore?: { ngnCredit: number; ngnOutstanding: number; usdtCredit: number; usdtOutstanding: number };
  balanceAfter?: { ngnCredit: number; ngnOutstanding: number; usdtCredit: number; usdtOutstanding: number };
  linkedTradeId?: number;
  linkedTradeSummary?: string; // e.g. "Buy 10,000 USDT @ ₦1,645"
};

// --- Settlement Entries ---

export type SettlementEntry = {
  id: number;
  tradeId: number;
  side: "coin" | "naira";
  amount: number;
  currency: "USDT" | "NGN";
  paymentMethod: "bank_transfer" | "cash" | "crypto_wallet" | "p2p";
  sourceDestination: string;
  reference: string;
  treasuryAccountId?: number;
  batchId?: string;
  isRefund?: boolean;
  isCreditToBalance?: boolean; // excess credited to counterparty balance (not a refund — doesn't reduce settled amounts)
  createdAt: string;
  middlemanLeg?: "buy" | "sell" | "profit"; // which leg of a middleman deal
  payerDetailId?: string;
  creditCounterpartyId?: number;
  crossCurrencyRate?: number;
  balanceDeltaNgn?: number;  // tracks balance change on counterparty for reversal on entry removal
  balanceDeltaUsdt?: number;
  sourceTradeId?: number;    // links credit application ↔ refund entries across trades
};

export type PayerDetail = {
  id: string;
  settlementId: number;
  tradeId: number;
  payerName: string;
  payerAccountNumber: string;
  payerBank: string;
  amount: number;
  reference: string;
  source: "ocr" | "manual" | "mono" | "treasury";
  ocrConfidence?: number;
  ocrRawText?: string;
  screenshotAttachmentId?: string;
  screenshotHash?: string;
  createdAt: string;
};

export type ReceiptFlag = {
  id: string;
  settlementId: number;
  tradeId: number;
  type: "duplicate_reference" | "duplicate_screenshot";
  severity: "warning" | "critical";
  message: string;
  details: Record<string, unknown>;
  createdAt: string;
  dismissed?: boolean;
};

export type OcrExtractedData = {
  amount: number | null;
  senderName: string | null;
  senderAccountNumber: string | null;
  senderBank: string | null;
  beneficiaryName: string | null;
  beneficiaryAccountNumber: string | null;
  sessionId: string | null;
  reference: string | null;
  transactionDate: string | null;
  confidence: number;
  rawText: string;
  bankDetected: string | null;
};

export const settlementEntries: SettlementEntry[] = [];

// --- Treasury Accounts ---

export type TreasuryAccount = {
  id: number;
  type: "bank" | "wallet";
  label: string;
  bankName?: string;
  accountNumber?: string;
  accountName?: string;
  address?: string;
  chain?: "tron" | "bsc" | "ethereum";
  balance: number;
  currency: "NGN" | "USDT" | "USDC";
  token?: "USDT" | "USDC";
  status?: "active" | "inactive";
  lastUpdated: string;
  // Nomba integration
  nombaCredentialId?: string;
  nombaAccountName?: string;
  nombaBankDetails?: {
    bankName: string;
    bankAccountNumber: string;
    bankAccountName: string;
  };
};

export const treasuryAccounts: TreasuryAccount[] = [];

/**
 * Hydrate the shared treasuryAccounts array from the persisted store.
 * Call this on mount in any page that uses the bank/wallet picker
 * so dynamically-added accounts (e.g. Nomba) appear everywhere.
 */
export async function hydrateTreasuryAccounts(): Promise<void> {
  try {
    const res = await fetch("/api/treasury/accounts");
    if (!res.ok) return;
    const data = await res.json();
    const accounts: TreasuryAccount[] = data.accounts ?? [];
    if (accounts.length > 0) {
      treasuryAccounts.length = 0;
      treasuryAccounts.push(...accounts);
    }
  } catch {
    // silently fall back to empty
  }
}

// --- Unified Bank Transactions ---

export type BankTransaction = {
  id: string;
  accountId: number;
  type: "credit" | "debit";
  amount: number;
  currency: "NGN" | "USDT";
  narration: string;
  date: string;
  // From TreasuryTransaction
  counterpartyName?: string;
  linkedTradeId?: number;
  linkedSettlementId?: number;
  loggedAt?: string;
  sessionId?: string;
  payerName?: string;
  payerAccount?: string;
  payerBank?: string;
  // From MonoBankStatement
  balance?: number;
  category?: string;
  source?: "mono" | "manual" | "seed" | "pdf_import" | "csv_import" | "excel_import" | "bank-ledger" | "trade-ledger";
  screenshotHash?: string;
  screenshotUrl?: string;
  loggedBy?: string;
  beneficiaryName?: string;
  beneficiaryAccountNumber?: string;
  note?: string;
  dismissedAt?: string;
  purpose?: "lend" | "borrow" | "return";
};

/** @deprecated Use BankTransaction instead */
export type TreasuryTransaction = BankTransaction;
/** @deprecated Use BankTransaction instead */
export type MonoBankStatement = BankTransaction;

export const bankTransactions: BankTransaction[] = [
  // GTBank Main (accountId: 1)
  { id: "mono_txn_gt000", accountId: 1, type: "credit", amount: 99_999_396, currency: "NGN", narration: "NIP/TRF FRM AHMED TRADING/AHMD20261010/USDT SETTLEMENT 73206", counterpartyName: "Ahmed", date: "2026-10-10T14:50:00Z", balance: 145_199_396, category: "transfer", source: "mono" },
  { id: "mono_txn_gt001", accountId: 1, type: "credit", amount: 8_240_000, currency: "NGN", narration: "NIP/TRF FRM BLESSING FX/1029384756/USDT PAYMENT", counterpartyName: "Blessing", linkedTradeId: 2, linkedSettlementId: 2, date: "2026-09-18T12:50:00Z", balance: 45_200_000, category: "transfer", source: "mono" },
  { id: "mono_txn_gt002", accountId: 1, type: "credit", amount: 3_500_000, currency: "NGN", narration: "NIP/TRF FRM MUSA ABDULLAHI/10020345678/BUSINESS PAYMENT", date: "2026-09-18T09:15:00Z", balance: 36_960_000, category: "transfer", source: "mono" },
  { id: "mono_txn_gt003", accountId: 1, type: "credit", amount: 20_000_000, currency: "NGN", narration: "NIP/TRF FRM FATIMA CRYPTO/9087654321/NGN20000000", counterpartyName: "Fatima", linkedTradeId: 5, linkedSettlementId: 4, date: "2026-09-17T15:32:00Z", balance: 33_460_000, category: "transfer", source: "mono" },
  { id: "mono_txn_gt004", accountId: 1, type: "debit", amount: 150_000, currency: "NGN", narration: "POS/WEB JUMIA PAYMENTS/LAGOS/000012345", date: "2026-09-17T14:20:00Z", balance: 13_460_000, category: "pos", source: "mono" },
  { id: "mono_txn_gt005", accountId: 1, type: "credit", amount: 10_000_000, currency: "NGN", narration: "NIP/TRF FRM AHMED TRADING/7654321098/SETTLEMENT", counterpartyName: "Ahmed", linkedTradeId: 7, linkedSettlementId: 8, date: "2026-09-16T14:23:00Z", balance: 13_610_000, category: "transfer", source: "mono" },
  { id: "mono_txn_gt006", accountId: 1, type: "debit", amount: 50_000, currency: "NGN", narration: "ATM/WDL/IKEJA BRANCH/000098765", date: "2026-09-16T10:05:00Z", balance: 3_610_000, category: "atm", source: "mono" },
  { id: "mono_txn_gt007", accountId: 1, type: "credit", amount: 6_500_000, currency: "NGN", narration: "NIP/TRF FRM IBRAHIM MOTORS/4455667788/INVOICE PAY", date: "2026-09-15T16:40:00Z", balance: 3_660_000, category: "transfer", source: "mono" },
  { id: "mono_txn_gt008", accountId: 1, type: "debit", amount: 5_200, currency: "NGN", narration: "CHARGE/SMS ALERT CHARGES/SEP 2026", date: "2026-09-15T00:01:00Z", balance: -2_840_000, category: "charge", source: "mono" },
  // GTBank seed-only entries (treasury-only, no mono match)
  { id: "seed_5", accountId: 1, type: "credit", amount: 20_000_000, currency: "NGN", narration: "TRF FROM FATIMA CRYPTO / 3210987654", counterpartyName: "Fatima", linkedTradeId: 11, linkedSettlementId: 13, date: "2026-09-14T15:01:30Z", source: "seed" },
  { id: "seed_6", accountId: 1, type: "debit", amount: 6_620_000, currency: "NGN", narration: "TRF TO CHIDI / GTBK-20260914-002", counterpartyName: "Chidi", linkedTradeId: 12, linkedSettlementId: 17, date: "2026-09-14T09:23:00Z", source: "seed" },
  { id: "seed_7", accountId: 1, type: "credit", amount: 8_000_000, currency: "NGN", narration: "TRF FROM FATIMA CRYPTO / 2109876543", counterpartyName: "Fatima", linkedTradeId: 15, linkedSettlementId: 21, date: "2026-09-13T08:10:00Z", source: "seed" },
  { id: "seed_8", accountId: 1, type: "credit", amount: 5_940_000, currency: "NGN", narration: "TRF FROM EMEKA / 6543210988", counterpartyName: "Emeka", linkedTradeId: 18, linkedSettlementId: 26, date: "2026-09-12T10:35:00Z", source: "seed" },
  { id: "seed_9", accountId: 1, type: "debit", amount: 1_200_000, currency: "NGN", narration: "POS WDL / IKEJA BRANCH", date: "2026-09-17T11:00:00Z", source: "seed" },

  // Access Bank Ops (accountId: 2)
  { id: "mono_txn_ac001", accountId: 2, type: "credit", amount: 12_800_000, currency: "NGN", narration: "NIP/TRF FRM FATIMA CRYPTO/9087654322/PART 2 SETTLEMENT", counterpartyName: "Fatima", linkedTradeId: 5, linkedSettlementId: 5, date: "2026-09-17T15:34:00Z", balance: 18_750_000, category: "transfer", source: "mono" },
  { id: "mono_txn_ac002", accountId: 2, type: "credit", amount: 5_000_000, currency: "NGN", narration: "NIP/TRF FRM LAGOS VENTURES LTD/INVOICE-2026-091", date: "2026-09-16T09:00:00Z", balance: 5_950_000, category: "transfer", source: "mono" },
  { id: "mono_txn_ac003", accountId: 2, type: "credit", amount: 4_200_000, currency: "NGN", narration: "NIP/TRF FRM AHMED TRADING/3344556677/OTC DEAL", date: "2026-09-15T11:30:00Z", balance: 950_000, category: "transfer", source: "mono" },
  { id: "mono_txn_ac004", accountId: 2, type: "debit", amount: 12_375_000, currency: "NGN", narration: "NIP/TRF TO BLESSING FX/5432109876/TRADE SETTLE", counterpartyName: "Blessing", linkedTradeId: 8, linkedSettlementId: 11, date: "2026-09-16T10:48:00Z", balance: -3_250_000, category: "transfer", source: "mono" },
  { id: "mono_txn_ac005", accountId: 2, type: "credit", amount: 7_800_000, currency: "NGN", narration: "NIP/TRF FRM CHIDI OBI/8899001122/REPAYMENT", date: "2026-09-14T08:45:00Z", balance: 9_125_000, category: "transfer", source: "mono" },
  { id: "mono_txn_ac006", accountId: 2, type: "debit", amount: 25_000, currency: "NGN", narration: "CHARGE/MAINTENANCE FEE/Q3 2026", date: "2026-09-14T00:01:00Z", balance: 1_325_000, category: "charge", source: "mono" },
  { id: "mono_txn_ac007", accountId: 2, type: "credit", amount: 2_300_000, currency: "NGN", narration: "NIP/TRF FRM KEMI ADEYEMI/5566778899/PERSONAL", date: "2026-09-13T15:20:00Z", balance: 1_350_000, category: "transfer", source: "mono" },
  { id: "mono_txn_ac008", accountId: 2, type: "debit", amount: 9_882_000, currency: "NGN", narration: "NIP/TRF TO AHMED TRADING/8765432109/BUY SETTLE", counterpartyName: "Ahmed", linkedTradeId: 14, linkedSettlementId: 19, date: "2026-09-13T12:33:00Z", balance: -950_000, category: "transfer", source: "mono" },

  // First Bank Reserve (accountId: 3)
  { id: "mono_txn_fb001", accountId: 3, type: "credit", amount: 15_000_000, currency: "NGN", narration: "NIP/TRF FRM FATIMA CRYPTO/3210987655/BULK SETTLE", counterpartyName: "Fatima", linkedTradeId: 11, linkedSettlementId: 14, date: "2026-09-14T15:02:00Z", balance: 62_000_000, category: "transfer", source: "mono" },
  { id: "mono_txn_fb002", accountId: 3, type: "credit", amount: 25_000_000, currency: "NGN", narration: "NIP/TRF FRM ABUJA CAPITAL PARTNERS/EQUITY RETURN", date: "2026-09-15T10:00:00Z", balance: 47_000_000, category: "transfer", source: "mono" },
  { id: "mono_txn_fb003", accountId: 3, type: "credit", amount: 8_500_000, currency: "NGN", narration: "NIP/TRF FRM BLESSING FX/7788990011/CRYPTO PROCEEDS", date: "2026-09-13T09:30:00Z", balance: 22_000_000, category: "transfer", source: "mono" },
  { id: "mono_txn_fb004", accountId: 3, type: "debit", amount: 100_000, currency: "NGN", narration: "ATM/WDL/ADEOLA ODEKU BRANCH/000054321", date: "2026-09-13T13:15:00Z", balance: 13_400_000, category: "atm", source: "mono" },
  { id: "mono_txn_fb005", accountId: 3, type: "credit", amount: 3_200_000, currency: "NGN", narration: "NIP/TRF FRM SUNDAY OKAFOR/2233445566/TRADE REFUND", date: "2026-09-12T14:50:00Z", balance: 13_500_000, category: "transfer", source: "mono" },
  { id: "mono_txn_fb006", accountId: 3, type: "debit", amount: 75_000, currency: "NGN", narration: "POS/WEB KONGA ONLINE/LAGOS/000067890", date: "2026-09-12T10:20:00Z", balance: 10_300_000, category: "pos", source: "mono" },
  { id: "mono_txn_fb007", accountId: 3, type: "credit", amount: 11_000_000, currency: "NGN", narration: "NIP/TRF FRM EMEKA HOLDINGS/6677889900/CONTRACT PAY", date: "2026-09-11T16:00:00Z", balance: 10_375_000, category: "transfer", source: "mono" },

  // Zenith Business (accountId: 4)
  { id: "mono_txn_zn001", accountId: 4, type: "credit", amount: 2_500_000, currency: "NGN", narration: "NIP/TRF FRM KANO ELECTRONICS/1122334455/SUPPLY PAYMENT", date: "2026-09-14T16:00:00Z", balance: 8_900_000, category: "transfer", source: "mono" },
  { id: "mono_txn_zn002", accountId: 4, type: "debit", amount: 8_000_000, currency: "NGN", narration: "NIP/TRF TO EMEKA/6543210987/TRADE SETTLEMENT", counterpartyName: "Emeka", linkedTradeId: 18, linkedSettlementId: 25, date: "2026-09-12T10:33:00Z", balance: 6_400_000, category: "transfer", source: "mono" },
  { id: "mono_txn_zn003", accountId: 4, type: "credit", amount: 4_800_000, currency: "NGN", narration: "NIP/TRF FRM FATIMA CRYPTO/0011223344/PARTIAL SETTLE", date: "2026-09-16T08:15:00Z", balance: 14_400_000, category: "transfer", source: "mono" },
  { id: "mono_txn_zn004", accountId: 4, type: "credit", amount: 1_650_000, currency: "NGN", narration: "NIP/TRF FRM AISHA BELLO/5544332211/PERSONAL TRF", date: "2026-09-15T12:30:00Z", balance: 9_600_000, category: "transfer", source: "mono" },
  { id: "mono_txn_zn005", accountId: 4, type: "debit", amount: 35_000, currency: "NGN", narration: "CHARGE/TOKEN MAINTENANCE/SEP 2026", date: "2026-09-15T00:01:00Z", balance: 7_950_000, category: "charge", source: "mono" },
  { id: "mono_txn_zn006", accountId: 4, type: "credit", amount: 9_200_000, currency: "NGN", narration: "NIP/TRF FRM AHMED TRADING/9988776655/BULK OTC", date: "2026-09-11T14:45:00Z", balance: 7_985_000, category: "transfer", source: "mono" },
  { id: "mono_txn_zn007", accountId: 4, type: "debit", amount: 200_000, currency: "NGN", narration: "POS/WEB SHOPRITE LEKKI/LAGOS/000011111", date: "2026-09-11T18:30:00Z", balance: -1_215_000, category: "pos", source: "mono" },

  // TRC-20 Wallet (id: 5) — USDT
  { id: "seed_18", accountId: 5, type: "debit", amount: 5_000, currency: "USDT", narration: "0x04b66961b2a6b001d4a62e10845d733650d8de6f75bfc422f9e1ca197b2093c5", counterpartyName: "Blessing", linkedTradeId: 2, linkedSettlementId: 1, date: "2026-09-18T12:46:00Z", source: "seed" },
];

/** @deprecated Use bankTransactions instead */
export const treasuryTransactions: BankTransaction[] = bankTransactions;
/** @deprecated Use bankTransactions instead */
export const monoBankStatements: BankTransaction[] = bankTransactions;

/**
 * Hydrate the shared bankTransactions array from the persisted store.
 * Call this on mount in any page that uses the bank statement picker
 * so newly-logged transactions (via Bank Ledger) appear everywhere.
 * Always re-fetches to pick up any newly-added transactions.
 */
export async function hydrateBankTransactions(): Promise<void> {
  try {
    const res = await fetch("/api/bank-transactions");
    if (!res.ok) return;
    const data = await res.json();
    const txs: BankTransaction[] = data.transactions ?? [];
    if (txs.length > 0) {
      bankTransactions.length = 0;
      bankTransactions.push(...txs);
    }
  } catch {
    // silently fall back to seed data
  }
}

// --- Staff Roles & Permissions ---

export type StaffRole = "owner" | "admin" | "trader" | "viewer";

export type Permission =
  | "log_trade"
  | "edit_trade"
  | "add_payment"
  | "remove_payment"
  | "select_from_bank"
  | "tag_treasury"
  | "change_direction"
  | "change_rate"
  | "dispute"
  | "view_audit_log"
  | "manage_staff"
  | "review_kyc"
  | "view_kyc"
  | "generate_kyc_link";

export const rolePermissions: Record<StaffRole, Permission[]> = {
  owner: ["log_trade", "edit_trade", "add_payment", "remove_payment", "select_from_bank", "tag_treasury", "change_direction", "change_rate", "dispute", "view_audit_log", "manage_staff", "review_kyc", "view_kyc", "generate_kyc_link"],
  admin: ["log_trade", "edit_trade", "add_payment", "remove_payment", "select_from_bank", "tag_treasury", "change_direction", "change_rate", "dispute", "view_audit_log", "review_kyc", "view_kyc", "generate_kyc_link"],
  trader: ["log_trade", "edit_trade", "add_payment", "select_from_bank", "tag_treasury", "change_direction", "change_rate", "view_kyc"],
  viewer: ["view_audit_log", "view_kyc"],
};

export type StaffMember = {
  id: number;
  name: string;
  email: string;
  role: StaffRole;
  isActive: boolean;
  lastActive: string;
};

export const staffMembers: StaffMember[] = [
  { id: 1, name: "Jeff Okonkwo", email: "jeff@jefftrading.com", role: "owner", isActive: true, lastActive: "2026-09-18T14:00:00Z" },
  { id: 2, name: "Aisha Mohammed", email: "aisha@jefftrading.com", role: "admin", isActive: true, lastActive: "2026-09-18T13:45:00Z" },
  { id: 3, name: "Tunde Bakare", email: "tunde@jefftrading.com", role: "trader", isActive: true, lastActive: "2026-09-18T12:30:00Z" },
  { id: 4, name: "Ngozi Eze", email: "ngozi@jefftrading.com", role: "viewer", isActive: true, lastActive: "2026-09-17T16:00:00Z" },
  { id: 5, name: "Yusuf Danjuma", email: "yusuf@jefftrading.com", role: "trader", isActive: false, lastActive: "2026-09-10T09:00:00Z" },
];

export const activeStaff: StaffMember = staffMembers[0]; // Jeff (owner)

export function hasPermission(staff: StaffMember, permission: Permission): boolean {
  return rolePermissions[staff.role].includes(permission);
}

// --- Audit Log ---

export type AuditChange = {
  field: string;
  from: string | number | boolean | null;
  to: string | number | boolean | null;
};

export type AuditLogEntry = {
  id: string;
  seq: number;
  tradeId?: number;
  staffId: number;
  staffName: string;
  action: string;
  details: string;
  changes: AuditChange[];
  counterpartyId?: number;
  channel: "dashboard" | "whatsapp" | "telegram" | "api";
  timestamp: string;
  prevHash: string;
  hash: string;
};

// Legacy type for seed data migration
export type LegacyAuditLogEntry = {
  id: number;
  tradeId?: number;
  staffId: number;
  staffName: string;
  action: string;
  details: string;
  channel: "dashboard" | "whatsapp" | "telegram" | "api";
  timestamp: string;
};

export const seedAuditLog: LegacyAuditLogEntry[] = [
  // Trade 2 — Blessing sell
  { id: 1, tradeId: 2, staffId: 1, staffName: "Jeff Okonkwo", action: "trade_logged", details: "Logged sell 5,000 USDT to Blessing at ₦1,648", channel: "dashboard", timestamp: "2026-09-18T12:45:00Z" },
  { id: 2, tradeId: 2, staffId: 2, staffName: "Aisha Mohammed", action: "payment_added", details: "Added ₦8,240,000 bank transfer via GTBank Main", channel: "dashboard", timestamp: "2026-09-18T12:50:00Z" },
  { id: 3, tradeId: 2, staffId: 2, staffName: "Aisha Mohammed", action: "payment_added", details: "Added 5,000 USDT crypto wallet via TRC-20 Hot Wallet", channel: "dashboard", timestamp: "2026-09-18T12:46:00Z" },
  { id: 4, tradeId: 2, staffId: 1, staffName: "Jeff Okonkwo", action: "status_changed", details: "Trade marked as settled", channel: "dashboard", timestamp: "2026-09-18T12:51:00Z" },
  // Trade 1 — Ahmed buy (pending)
  { id: 5, tradeId: 1, staffId: 3, staffName: "Tunde Bakare", action: "trade_logged", details: "Logged buy 10,000 USDT from Ahmed at ₦1,645", channel: "dashboard", timestamp: "2026-09-18T13:14:03Z" },
  { id: 6, tradeId: 1, staffId: 3, staffName: "Tunde Bakare", action: "payment_added", details: "Added 10,000 USDT crypto wallet via TRC-20 Hot Wallet", channel: "dashboard", timestamp: "2026-09-18T13:14:03Z" },
  // Trade 5 — Fatima sell
  { id: 7, tradeId: 5, staffId: 1, staffName: "Jeff Okonkwo", action: "trade_logged", details: "Logged sell 20,000 USDT to Fatima at ₦1,640", channel: "whatsapp", timestamp: "2026-09-17T15:30:00Z" },
  { id: 8, tradeId: 5, staffId: 2, staffName: "Aisha Mohammed", action: "payment_added", details: "Added ₦20,000,000 bank transfer via GTBank Main", channel: "dashboard", timestamp: "2026-09-17T15:32:00Z" },
  { id: 9, tradeId: 5, staffId: 2, staffName: "Aisha Mohammed", action: "payment_added", details: "Added ₦12,800,000 bank transfer via Access Bank Ops", channel: "dashboard", timestamp: "2026-09-17T15:34:00Z" },
  { id: 10, tradeId: 5, staffId: 1, staffName: "Jeff Okonkwo", action: "payment_added", details: "Added 20,000 USDT crypto wallet via TRC-20 Hot Wallet", channel: "dashboard", timestamp: "2026-09-17T15:31:00Z" },
  // Trade 7 — Ahmed sell
  { id: 11, tradeId: 7, staffId: 3, staffName: "Tunde Bakare", action: "trade_logged", details: "Logged sell 12,000 USDT to Ahmed at ₦1,642", channel: "dashboard", timestamp: "2026-09-16T14:20:00Z" },
  { id: 12, tradeId: 7, staffId: 3, staffName: "Tunde Bakare", action: "rate_updated", details: "Rate changed from ₦1,640 to ₦1,642", channel: "dashboard", timestamp: "2026-09-16T14:21:00Z" },
  { id: 13, tradeId: 7, staffId: 2, staffName: "Aisha Mohammed", action: "bank_txns_applied", details: "Applied 1 bank transaction — ₦10,000,000 from GTBank Main", channel: "dashboard", timestamp: "2026-09-16T14:23:00Z" },
  // Trade 11 — Fatima sell (large)
  { id: 14, tradeId: 11, staffId: 1, staffName: "Jeff Okonkwo", action: "trade_logged", details: "Logged sell 30,000 USDT to Fatima at ₦1,638", channel: "telegram", timestamp: "2026-09-14T15:00:00Z" },
  { id: 15, tradeId: 11, staffId: 2, staffName: "Aisha Mohammed", action: "payment_added", details: "Added ₦20,000,000 bank transfer via GTBank Main", channel: "dashboard", timestamp: "2026-09-14T15:01:30Z" },
  { id: 16, tradeId: 11, staffId: 2, staffName: "Aisha Mohammed", action: "payment_added", details: "Added ₦15,000,000 bank transfer via First Bank Reserve", channel: "dashboard", timestamp: "2026-09-14T15:02:00Z" },
  // Trade 15 — disputed
  { id: 17, tradeId: 15, staffId: 1, staffName: "Jeff Okonkwo", action: "trade_logged", details: "Logged buy 10,000 USDT from Fatima at ₦1,635", channel: "dashboard", timestamp: "2026-09-13T08:00:00Z" },
  { id: 18, tradeId: 15, staffId: 3, staffName: "Tunde Bakare", action: "payment_added", details: "Added ₦8,000,000 bank transfer via GTBank Main", channel: "dashboard", timestamp: "2026-09-13T08:10:00Z" },
  { id: 19, tradeId: 15, staffId: 1, staffName: "Jeff Okonkwo", action: "status_changed", details: "Trade disputed — naira payment incomplete", channel: "dashboard", timestamp: "2026-09-13T09:00:00Z" },
  // Trade 8 — Blessing buy
  { id: 20, tradeId: 8, staffId: 3, staffName: "Tunde Bakare", action: "trade_logged", details: "Logged buy 7,500 USDT from Blessing at ₦1,650", channel: "dashboard", timestamp: "2026-09-16T10:45:00Z" },
];

// --- Disputes ---

export const DISPUTE_REASONS = [
  "Payment not received",
  "Wrong amount sent",
  "Delayed settlement",
  "Unauthorized trade",
  "Rate disagreement",
  "Counterparty unresponsive",
  "Fraudulent activity",
  "Other",
] as const;

export type DisputeReason = (typeof DISPUTE_REASONS)[number];
export type DisputePriority = "low" | "medium" | "high" | "critical";
export type DisputeStatus = "open" | "under_review" | "resolved" | "dismissed";

export type DisputeResolution = {
  type: "refund" | "partial_refund" | "credit_to_balance" | "dismissed";
  amount?: number;
  currency?: "NGN" | "USDT";
  note: string;
  resolvedBy: string;
  resolvedAt: string;
};

export type DisputeComment = {
  id: string;
  author: string;
  text: string;
  createdAt: string;
};

export type DisputeHistoryEntry = {
  action: string; // "status_changed" | "comment_added" | "evidence_added" | "evidence_removed" | "reopened"
  from?: string | null;
  to?: string | null;
  by: string;
  at: string;
};

export type Dispute = {
  id: string;
  tradeId: number;
  counterpartyId: number;
  counterpartyName: string;
  reason: string;
  description: string;
  priority: DisputePriority;
  status: DisputeStatus;
  resolution: DisputeResolution | null;
  evidence: string[];
  comments: DisputeComment[];
  history: DisputeHistoryEntry[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export const seedDisputes: Dispute[] = [
  {
    id: "disp-1694592000000-x7k2m1",
    tradeId: 15,
    counterpartyId: 6,
    counterpartyName: "Fatima",
    reason: "Payment not received",
    description: "Naira payment incomplete — ₦6,350,000 outstanding after 24 hours.",
    priority: "high",
    status: "open",
    resolution: null,
    evidence: [],
    comments: [],
    history: [{ action: "status_changed", from: null, to: "open", by: "Jeff Okonkwo", at: "2026-09-13T09:00:00Z" }],
    createdBy: "Jeff Okonkwo",
    createdAt: "2026-09-13T09:00:00Z",
    updatedAt: "2026-09-13T09:00:00Z",
  },
];
